/**
 * Reset — request, sent, set a new one, and the expired link.
 *
 * Visual Reference: `design/screens/auth-reset.html`. Its note carries the two
 * rules that matter:
 *
 *   · "The sent card names the address without confirming an account exists for
 *     it" — so the copy is "If an account exists for {email}…". A screen that
 *     said "we've sent you a link" would confirm the account, turning the reset
 *     form into the account-existence oracle the login form is careful not to
 *     be.
 *   · "Resend is disabled on a visible countdown rather than silently rate
 *     limited."
 *
 * The lifetime sentence — "The link works for one hour" — is rendered from
 * `LINK_LIFETIME_SECONDS.reset`, the same constant the email template uses, so
 * the screen and the mail cannot promise different hours.
 *
 * Story 3.4.
 */
import { useState, type FormEvent } from 'react'
import { LINK_LIFETIME_SECONDS, PASSWORD_MIN_LENGTH, RESEND_COOLDOWN_SECONDS } from '@leadgible/schema'
import { AuthCard, AuthFootnote, AuthLink, CardHeading } from './AuthPanel'
import { Button } from '../ui/Button'
import { Callout } from '../ui/Callout'
import { Field } from '../ui/Field'
import { useCountdown } from '../ui/useCountdown'
import { apiPost, ApiFailure } from '../lib/api'

type Step = 'request' | 'sent' | 'set-new' | 'expired'

const RESET_HOURS = LINK_LIFETIME_SECONDS.reset / 3600

export function ResetPassword() {
  const token = new URLSearchParams(window.location.search).get('token')
  const [step, setStep] = useState<Step>(token ? 'set-new' : 'request')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [failure, setFailure] = useState<ApiFailure | null>(null)
  const { remaining, start } = useCountdown()

  async function requestLink(event?: FormEvent) {
    event?.preventDefault()
    setSubmitting(true)
    setFailure(null)
    try {
      await apiPost('/v1/auth/request-password-reset', { email, redirectTo: '/reset' })
      setStep('sent')
      start(RESEND_COOLDOWN_SECONDS)
    } catch (error) {
      // A refusal must NOT reveal whether the address is known — the sent card
      // is shown either way, which is the same reasoning as its copy.
      if (error instanceof ApiFailure && error.code === 'rate_limited') setFailure(error)
      else {
        setStep('sent')
        start(RESEND_COOLDOWN_SECONDS)
      }
    } finally {
      setSubmitting(false)
    }
  }

  async function saveNewPassword(event: FormEvent) {
    event.preventDefault()
    setSubmitting(true)
    setFailure(null)
    try {
      await apiPost('/v1/auth/reset-password', { newPassword: password, token })
      window.location.assign('/login')
    } catch (error) {
      const failed = error instanceof ApiFailure ? error : null
      setFailure(failed)
      // "That link has expired. Ask for a new one." is its own card, not an
      // error above a form the person can no longer use.
      if (failed && /expired|no longer valid/i.test(failed.message)) setStep('expired')
    } finally {
      setSubmitting(false)
    }
  }

  if (step === 'sent') {
    return (
      // Centred contents at 14px — the mockup's sent card, measured. DECISION-039.
      <AuthCard gap="gap-mdx-14">
        <CardHeading title="Check your email">
          If an account exists for {email || 'that address'}, a reset link is on its way. The link
          works for {RESET_HOURS === 1 ? 'one hour' : `${RESET_HOURS} hours`}.
        </CardHeading>
        {failure ? <Callout tone="notice">{failure.message}</Callout> : null}
        <div className="flex flex-col gap-mdx-20">
          <Button
            full
            variant="secondary"
            disabled={remaining > 0}
            loading={submitting ? 'Sending again' : undefined}
            onClick={() => void requestLink()}
          >
            {remaining > 0 ? `Resend in ${remaining}s` : 'Resend the link'}
          </Button>
          <AuthFootnote>Nothing arrived? Check spam, then send it again.</AuthFootnote>
          <AuthFootnote>
            <AuthLink href="/login">Back to log in</AuthLink>
          </AuthFootnote>
        </div>
      </AuthCard>
    )
  }

  if (step === 'expired') {
    return (
      // `48px 28px` and centred — the one card in the family with its own
      // padding, and `--mdx-space-48` is named "error state vertical padding".
      <AuthCard gap="gap-mdx-14" pad="px-mdx-28 py-mdx-48">
        <Callout>That link has expired. Ask for a new one.</Callout>
        <CardHeading title="That link has expired">
          Reset links last {RESET_HOURS === 1 ? 'one hour' : `${RESET_HOURS} hours`}. Your password
          hasn't changed, so the old one still works if you remember it.
        </CardHeading>
        <Button full onClick={() => setStep('request')}>
          Start over
        </Button>
      </AuthCard>
    )
  }

  if (step === 'set-new') {
    const mismatch = confirm.length > 0 && confirm !== password
    return (
      <AuthCard align="left" gap="gap-mdx-20">
        <CardHeading title="Choose a new password" />
        <form className="flex flex-col gap-mdx-20" onSubmit={saveNewPassword} noValidate>
          {failure ? <Callout>{failure.message}</Callout> : null}
          <Field
            label="New password"
            type="password"
            name="newPassword"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            helper={`Use ${PASSWORD_MIN_LENGTH} characters or more. A short phrase is stronger than a scramble.`}
          />
          <Field
            label="Type it again"
            type="password"
            name="confirmPassword"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            error={mismatch ? 'Those two do not match. Retype the second one.' : undefined}
          />
          <Button
            type="submit"
            full
            disabled={mismatch || password.length < PASSWORD_MIN_LENGTH}
            loading={submitting ? 'Saving your password' : undefined}
          >
            Save new password
          </Button>
        </form>
      </AuthCard>
    )
  }

  return (
    <AuthCard align="left" gap="gap-mdx-20">
      <CardHeading title="Reset your password">We'll email you a link.</CardHeading>
      <form className="flex flex-col gap-mdx-20" onSubmit={requestLink} noValidate>
        {failure ? <Callout tone="notice">{failure.message}</Callout> : null}
        <Field
          label="Email"
          type="email"
          name="email"
          autoComplete="email"
          placeholder="you@company.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Button type="submit" full loading={submitting ? 'Sending the link' : undefined}>
          Send the link
        </Button>
        <AuthFootnote>
          <AuthLink href="/login">Back to log in</AuthLink>
        </AuthFootnote>
      </form>
    </AuthCard>
  )
}
